import React, { useCallback, useEffect, useMemo, useState } from "react";
import TableContainer from "../../components/Common/TableContainer";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { Card, CardBody, Button, Container } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { listPageData } from "./DispatchedFile";
import useFetchData from "../../custom/Utils/UseFetchDataUtils";
import usePagination from "../../custom/Utils/UsePaginationUtils";
import useListFilters from "../../custom/Utils/UseListFiltersUtils";
import SmartImportButton from "../../custom/components/Import/SmartImportButton.js";
import {
  areAllValuesIdentical,
  smartSort,
} from "../../custom/Utils/ColumnUtils";

const breadcrumbItems = [];

const ScrapList = () => {
  const navigate = useNavigate();
  const [exportData, setExportData] = useState([]);
  const getOnlyDate = (date) => {
    if (!date) return "-";

    const d = new Date(date);
    if (isNaN(d.getTime())) return "-";

    return d
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "-");
  };

  const { payload, endpoint, searchfields, exportFileName, filterConfig } =
    listPageData();
  const initialPayload = useMemo(() => payload, []);

  // pagination
  const {
    page,
    pageSize,
    nextPage,
    previousPage,
    lastPage,
    firstPage,
    handlePageSizeChange,
    setPage,
  } = usePagination();

  // list filters (config comes from DispatchedFile.js)
  const {
    filters,
    filterValues,
    handleFilterChange,
    resetFilters,
    filterWhereCondition,
    appliedFilterCount,
  } = useListFilters(filterConfig);

  // fetch data using the provided payload, page, and page size
  const { data, isLoading, setSearchKeyword, totalCount, refetch } = useFetchData(
    initialPayload,
    page,
    pageSize,
    endpoint,
    searchfields,
    { filterWhere: filterWhereCondition, onResetPage: firstPage }
  );

  const handleClickAdd = () => {
    navigate("/dispatched-create");
  };

  const handleViewClick = (id) => {
    navigate(`/dispatched-view/${id}`);
  };

  const handlePageSizeChangeWrapper = useCallback(
    (newSize) => {
      handlePageSizeChange(newSize, totalCount);
    },
    [totalCount],
  );

  const columns = useMemo(
    () => [
      {
        Header: "SR.NO",
        accessor: "index",
        accessor: (row) => page * pageSize + row.index,
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "50px" }}>{value}</div>,
      },
      {
        Header: "Dispatch No",
        accessor: (row) =>
          row?.dispatched_no && row?.dispatched_no ? row?.dispatched_no : "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "JOB CARD",
        accessor: (row) =>
          row?.intake && row?.job_card ? row?.job_card?.job_card_no : "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "GMDS CODE",
        accessor: (row) =>
          row?.intake && row?.intake?.material
            ? row?.intake?.material?.gmds_code || row?.intake?.material?.name
            : "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },

      {
        Header: "INTAKE",
        accessor: (row) =>
          row?.intake && row?.intake ? row?.intake?.intake_no : "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "SERIAL NO",
        accessor: (row) =>
          row?.intake && row?.intake ? row?.intake?.material_serial_no : "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },

      {
        Header: "DISPATCHED DATE",
        accessor: (row) =>
          row?.dispatch_date && row?.dispatch_date
            ? getOnlyDate(row?.dispatch_date)
            : "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "DISPATCHED BY",
        accessor: (row) =>
          row?.dispatched_by_user && row?.dispatched_by_user
            ? `${row?.dispatched_by_user?.first_name} ${row?.dispatched_by_user?.last_name}`
            : "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "REMARK",
        accessor: (row) => (row?.remark && row?.remark ? row?.remark : "-"),
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "IS INVOICE GENERATED",
        accessor: (row) => (row?.is_invoice_generated == true ? "YES" : "NO"),
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },

      {
        Header: "STATUS",
        accessor: "is_active",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        disableSortBy: areAllValuesIdentical(data, "is_active"),
        Cell: ({ value }) => (
          <div style={{ width: "100px" }}>
            {value === false || value === 0 ? (
              <span className="badge bg-danger-subtle text-danger text-uppercase">
                In-active
              </span>
            ) : (
              <span className="badge bg-success-subtle text-success text-uppercase">
                Active
              </span>
            )}
          </div>
        ),
      },
      {
        Header: "ACTION",
        accessor: "actions",
        disableFilters: true,
        filterable: false,
        Cell: ({ row }) => (
          <div style={{ width: "150px", display: "flex" }}>
            <div
              onClick={() => {
                handleViewClick(row.original.id);
              }}
            >
              <Link className="me-3 text-primary">
                <i className="mdi mdi-eye font-size-18"></i>
              </Link>
            </div>
          </div>
        ),
      },
    ],
    [page, pageSize, data],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <Breadcrumb
              title="Dispatched List"
              breadcrumbItems={breadcrumbItems}
            />
            <Button
              color="primary"
              className="waves-effect waves-light me-1"
              onClick={handleClickAdd}
              style={{ marginLeft: "auto", background: "#006eb9" }}
            >
              Dispatch Job Card
            </Button>
            <SmartImportButton
              module="dispatched"
              label="Dispatched"
              onImported={refetch}
            />
          </div>

          <Card>
            <CardBody>
              <TableContainer
                columns={columns || []}
                data={data || []}
                isLoading={isLoading}
                onSearchInputData={setSearchKeyword}
                pageIndexProp={page}
                onPageSizeChange={handlePageSizeChangeWrapper}
                nextToPage={nextPage}
                lastToPage={() => lastPage(totalCount)}
                previousToPage={previousPage}
                previousFirstToPage={firstPage}
                totalCount={totalCount}
                customPageSize={pageSize}
                filters={filters}
                filterValues={filterValues}
                onFilterChange={handleFilterChange}
                onFilterReset={resetFilters}
                appliedFilterCount={appliedFilterCount}
                exportData={exportData}
                exportFileName={exportFileName}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ScrapList;
